// @ts-nocheck
import * as React from "react";
import {
  setAccessToken,
  setTenantId as setGlobalTenantId,
} from "@shared/lib/auth";
import {
  clearStoredTenant,
  TENANT_CHANGED_EVENT,
} from "@shared/lib/tenantStorage";
import {
  AUTH_KEY,
  AUTH_LOGOUT_BROADCAST_KEY,
  broadcastAuthLogout,
  clearAuth,
  loadAuth,
  parseAuthLogoutBroadcast,
  saveAuth,
  type AuthLogoutReason,
  type StoredUser,
} from "@shared/lib/tokenStorage";
import { logout as apiLogout } from "@shared/lib/api/auth";
import { unsubscribeAdminPushOnLogout } from "@features/notifications/api/notifications";
import { translateUi } from "@i18n/config";

export type AuthState = {
  user: StoredUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  // Epoch milliseconds when the access token expires.
  expiresAt: number | null;
  tenantId: string | null;
};

type LoginInput = {
  accessToken: string;
  refreshToken?: string | null;
  expiresAt?: number | null;
  user?: StoredUser | null;
  remember?: boolean;
  tenantId?: string | null;
};

type LogoutOptions = {
  reason?: AuthLogoutReason;
  broadcast?: boolean;
  server?: boolean;
};

type AuthContextValue = AuthState & {
  login: (input: LoginInput) => void;
  logout: (options?: LogoutOptions) => void | Promise<void>;
  setUser: (user: StoredUser | null) => void;
  getToken: () => string | null;
};

type AuthLogoutEventDetail = {
  reason?: AuthLogoutReason;
  source?: "local" | "storage";
};

const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined,
);

// Legacy local mirror retained to preserve the previous provider behavior.
// The HTTP client itself reads the value managed by setAccessToken().
let latestToken: string | null = null;

function createEmptyAuthState(): AuthState {
  return {
    user: null,
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    tenantId: null,
  };
}

function getEventTenantId(
  detail: { tenantId?: string | null } | undefined,
): string | null {
  return typeof detail?.tenantId === "string" && detail.tenantId.trim()
    ? detail.tenantId
    : null;
}

function getExpiresAt(payload: {
  expiresAt?: unknown;
  expiresIn?: any;
}): number | null {
  if (typeof payload.expiresAt === "number") return payload.expiresAt;
  return payload.expiresIn ? Date.now() + payload.expiresIn * 1000 : null;
}

function detectRememberPreference(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const stored = loadAuth();
    if (typeof stored?.remember === "boolean") return stored.remember;
    return Boolean(window.localStorage.getItem(AUTH_KEY));
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Keep React state, module-level request state, browser storage, and legacy state aligned.
  const clearClientAuthState = React.useCallback(() => {
    latestToken = null;
    setAccessToken(null);
    setGlobalTenantId(null);
    clearStoredTenant();

    if (typeof window !== "undefined") {
      try {
        (window as any).__token = null;
        window.dispatchEvent(new CustomEvent("auth:session-cleared"));
      } catch {
        // Legacy cleanup must not prevent logout.
      }
    }

    clearAuth();
    setState(createEmptyAuthState());
  }, []);

  const [state, setState] = React.useState<AuthState>(() => {
    const stored = loadAuth();

    // Prime the shared HTTP client before children render.
    latestToken = stored?.accessToken ?? null;
    setAccessToken(latestToken);
    setGlobalTenantId(stored?.tenantId ?? null);

    return stored ?? createEmptyAuthState();
  });

  const setUser = React.useCallback((user: StoredUser | null) => {
    setState((current) => {
      const next = { ...current, user };
      saveAuth(next);
      return next;
    });
  }, []);

  const login = React.useCallback((input: LoginInput) => {
    const next: AuthState = {
      user: input.user ?? null,
      accessToken: input.accessToken ?? null,
      refreshToken: input.refreshToken ?? null,
      expiresAt: input.expiresAt ?? null,
      tenantId: input.tenantId ?? null,
    };

    latestToken = next.accessToken;
    setAccessToken(next.accessToken);
    setGlobalTenantId(next.tenantId);
    saveAuth(next, { remember: input.remember });
    setState(next);
  }, []);

  const logout = React.useCallback(
    async (options: LogoutOptions = {}) => {
      // Read this before cleanup, because clearClientAuthState removes it from storage and state.
      const refreshToken = state.refreshToken;
      const reason = options.reason ?? "manual";

      await unsubscribeAdminPushOnLogout();
      clearClientAuthState();

      if (options.broadcast !== false) {
        broadcastAuthLogout(reason);
      }

      if (options.server === false) return;

      try {
        await apiLogout(refreshToken || null);
      } catch {
        // Local logout already succeeded; a failed server request should not restore the session.
      }
    },
    [clearClientAuthState, state.refreshToken],
  );

  const getToken = React.useCallback(() => {
    // Some older consumers still set this global token directly.
    const legacyToken =
      typeof window !== "undefined"
        ? ((window as any).__token as string | null)
        : null;
    const token = state.accessToken ?? legacyToken;

    if (!token) return null;
    if (state.expiresAt && Date.now() >= state.expiresAt) return null;

    return token;
  }, [state.accessToken, state.expiresAt]);

  const value = React.useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, setUser, getToken }),
    [state, login, logout, setUser, getToken],
  );

  // Events from the tenant provider and SDK are translated into updates to this React context.
  React.useEffect(() => {
    function handleTenantChanged(event: Event) {
      const detail = (
        event as CustomEvent<{ tenantId?: string | null }> | undefined
      )?.detail;
      const tenantId = getEventTenantId(detail);

      setGlobalTenantId(tenantId);
      setState((current) => {
        if (current.tenantId === tenantId) return current;

        const next = { ...current, tenantId };
        if (next.accessToken || next.refreshToken) {
          saveAuth(next);
        }
        return next;
      });
    }

    function handleTokensRefreshed(event: Event) {
      const tokens = (event as CustomEvent<any>).detail || {};

      login({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken ?? state.refreshToken,
        user: tokens.user ?? state.user,
        expiresAt: getExpiresAt(tokens),
        tenantId:
          typeof tokens.tenantId !== "undefined"
            ? tokens.tenantId
            : state.tenantId,
        remember: detectRememberPreference(),
      });
    }

    function handleLogoutEvent(event: Event) {
      const detail = (event as CustomEvent<AuthLogoutEventDetail> | undefined)
        ?.detail;

      // A storage-triggered logout was already broadcast by another tab.
      if (detail?.source === "storage") {
        clearClientAuthState();
        return;
      }

      const reason = detail?.reason ?? "manual";
      logout({
        reason,
        server: reason === "session-expired" ? false : undefined,
      });
    }

    if (typeof window === "undefined") return;

    window.addEventListener(TENANT_CHANGED_EVENT, handleTenantChanged as any);
    window.addEventListener(
      "auth:tokens-refreshed",
      handleTokensRefreshed as any,
    );
    window.addEventListener("auth:logout", handleLogoutEvent);

    return () => {
      window.removeEventListener(
        TENANT_CHANGED_EVENT,
        handleTenantChanged as any,
      );
      window.removeEventListener(
        "auth:tokens-refreshed",
        handleTokensRefreshed as any,
      );
      window.removeEventListener("auth:logout", handleLogoutEvent);
    };
  }, [
    clearClientAuthState,
    login,
    logout,
    state.refreshToken,
    state.tenantId,
    state.user,
  ]);

  // Keep this tab synchronized with auth changes made in a different tab.
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    function applyStoredAuth() {
      const stored = loadAuth();

      if (!stored?.accessToken) {
        clearClientAuthState();
        return;
      }

      const next: AuthState = {
        user: stored.user ?? null,
        accessToken: stored.accessToken ?? null,
        refreshToken: stored.refreshToken ?? null,
        expiresAt: stored.expiresAt ?? null,
        tenantId:
          typeof stored.tenantId !== "undefined" ? stored.tenantId : null,
      };

      latestToken = next.accessToken;
      setAccessToken(next.accessToken);
      setGlobalTenantId(next.tenantId);
      setState(next);
    }

    function handleStorageChange(event: StorageEvent) {
      if (event.key === AUTH_LOGOUT_BROADCAST_KEY) {
        const broadcast = parseAuthLogoutBroadcast(event.newValue) ?? {
          reason: "unknown" as AuthLogoutReason,
          at: Date.now(),
          nonce: "",
        };

        window.dispatchEvent(
          new CustomEvent("auth:logout", {
            detail: { reason: broadcast.reason, source: "storage" },
          }),
        );
        return;
      }

      if (event.key === AUTH_KEY) {
        applyStoredAuth();
      }
    }

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [clearClientAuthState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context)
    throw new Error(
      translateUi(
        "useAuthMustBeUsedWithinAuthProvider",
        "useAuth must be used within AuthProvider",
      ),
    );
  return context;
}
